import { useState } from "react";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { Lock, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { gaEvent, trackSignUp } from "../../lib/ga";
import { api, setAuthToken } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

/**
 * Inline sign-up / log-in card shown in place of the quiz result. The quiz
 * answers stay in React state + localStorage; once the visitor is
 * authenticated we call `onAuthed()` so the parent can save the answers to
 * the new account and reveal the result.
 */
export function QuizAuthGate({ ventureKind = "business", noun = "business", onAuthed, onBack }) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("signup"); // signup | login
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(null); // email awaiting verification
  const [resent, setResent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email: pending }); setResent(true); } catch (_) { /* ignore */ }
  };

  if (pending) {
    return (
      <section data-testid="quiz-auth-gate" className="mt-10">
        <div className="mx-auto max-w-md rounded-2xl border border-emerald-400/25 bg-white/[0.04] p-6 text-center" data-testid="quiz-auth-check-inbox">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30"><Sparkles className="h-6 w-6 text-emerald-400" /></div>
          <h2 className="mt-4 text-2xl font-extrabold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Check your inbox</h2>
          <p className="mt-2 text-sm text-white/70">We sent a verification link to <span className="font-semibold text-white" data-testid="quiz-auth-check-inbox-email">{pending}</span>. Click it to activate your account — your quiz result will be waiting for you.</p>
          <p className="mt-2 text-xs text-white/50">Your answers are saved in this browser, so open the link here to see them applied.</p>
          <button type="button" onClick={resend} disabled={resent} data-testid="quiz-auth-resend"
            className="mt-5 w-full rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">{resent ? "Sent — check again" : "Resend verification email"}</button>
        </div>
      </section>
    );
  }

  const finish = async (data) => {
    if (data?.token) setAuthToken(data.token);
    await refresh();
    await onAuthed?.(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/signup", {
          name: form.name, email: form.email, password: form.password,
          role: "founder", venture_kind: ventureKind, quiz_gate: true,
        });
        trackSignUp("email", { source: "quiz_gate" });
        if (data?.verification_required) {
          try { localStorage.setItem("clinq_pending_signup_role", ventureKind === "project" ? "project" : "founder"); } catch (_) { /* ignore */ }
          setPending(data.email); return;
        }
        await finish(data);
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        if (data.role !== "founder") {
          setErr("This quiz result is for founder accounts. Please sign in with (or create) a founder account.");
          setAuthToken(null);
          return;
        }
        gaEvent("login", { method: "email", source: "quiz_gate" });
        await finish(data);
      }
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const onGoogleSuccess = async (credResp) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/google", {
        credential: credResp.credential, role: "founder", venture_kind: ventureKind,
      });
      trackSignUp("google", { source: "quiz_gate" });
      await finish(data);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Google sign-in failed");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-white/15 bg-white/[0.05] px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-emerald-400/70";

  return (
    <section data-testid="quiz-auth-gate" className="mt-10">
      <div className="text-center">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F59E0B]/15 ring-2 ring-[#F59E0B]/30">
          <Lock className="h-6 w-6 text-[#F59E0B]" />
        </div>
        <h1 className="mt-5 font-extrabold leading-[1.1] tracking-tight text-white"
            style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}>
          Your preliminary result is <span className="text-emerald-400">ready</span>.
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-base text-white/70">
          Create a free account (or sign in) to reveal your {ventureKind === "project" ? "Project Readiness" : "Capital Readiness"} result and see which funders are likely to back your {noun}.
        </p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-300/90">
          <Sparkles className="h-3.5 w-3.5" /> Your answers are saved to your profile automatically.
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-md rounded-2xl border border-emerald-400/25 bg-white/[0.04] p-6" data-testid="quiz-auth-card">
        <div className="grid grid-cols-2 rounded-lg bg-white/[0.06] p-1 text-sm font-semibold">
          {[["signup", "Create free account"], ["login", "I have an account"]].map(([m, label]) => (
            <button key={m} type="button" data-testid={`quiz-auth-tab-${m}`}
              onClick={() => { setMode(m); setErr(""); }}
              className={"rounded-md px-3 py-2 transition-colors " + (mode === m ? "bg-[#0A8B85] text-white" : "text-white/60 hover:text-white")}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode === "signup" && (
            <label className="block text-xs font-semibold text-white/60">Full name
              <input required value={form.name} onChange={set("name")} placeholder="Jane Wanjiru" className={inputCls} data-testid="quiz-auth-name" />
            </label>
          )}
          <label className="block text-xs font-semibold text-white/60">Email
            <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="quiz-auth-email" />
          </label>
          <label className="block text-xs font-semibold text-white/60">Password
            <input required type="password" minLength={6} value={form.password} onChange={set("password")}
              placeholder={mode === "signup" ? "At least 6 characters" : "Your password"} className={inputCls} data-testid="quiz-auth-password" />
          </label>
          {mode === "signup" && (
            <label className="flex items-start gap-2 text-xs text-white/60" data-testid="quiz-auth-terms">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 accent-[#0A8B85]" data-testid="quiz-auth-terms-checkbox" />
              <span>I agree to the <Link to="/terms" target="_blank" className="font-semibold text-emerald-300 hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="font-semibold text-emerald-300 hover:underline">Privacy Policy</Link>.</span>
            </label>
          )}
          {err && <p className="rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="quiz-auth-error">{err}</p>}
          <button type="submit" disabled={busy || (mode === "signup" && !agreed)} data-testid="quiz-auth-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#F59E0B] px-6 py-3 text-sm font-semibold text-[#04302E] shadow-lg shadow-black/30 transition-all hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "signup" ? "Create account & reveal my result" : "Sign in & reveal my result"}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-white/35">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="flex justify-center overflow-hidden" data-testid="quiz-auth-google">
          {(mode === "login" || agreed) ? (
            <GoogleLogin onSuccess={onGoogleSuccess} onError={() => setErr("Google sign-in was cancelled or failed.")}
              text={mode === "signup" ? "signup_with" : "signin_with"} shape="rectangular" theme="outline" size="large"
              width={Math.min(352, (typeof window !== "undefined" ? window.innerWidth : 352) - 96)} />
          ) : (
            <button type="button" data-testid="quiz-auth-google-blocked" onClick={() => setErr("Please accept the Terms of Service first.")}
              className="w-full rounded-md border border-white/20 py-2.5 text-sm font-semibold text-white/60">
              Continue with Google
            </button>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-white/45">Free to start · NDA-protected uploads · Built for African SMEs</p>
      </div>

      {onBack && (
        <button data-testid="quiz-auth-back" onClick={onBack} className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}
    </section>
  );
}
